class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.overlay = this.querySelector('.cart-notification-overlay');
    this.header = document.querySelector('sticky-header');
    this.upsell = document.getElementById('cart-notification-upsell');
    this.upsellPanel = document.getElementById('cart-notification-upsell-panel');
    this.tabCache = new Map();
    this.activeTabKey = null;
    this.productId = null;
    this.lineItemId = null;
    this._upsellRequestId = 0;
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onOverlayClick = this.close.bind(this);
    this.onUpsellClick = this.handleUpsellClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"].cart-notification__close').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    if (this.upsell) {
      this.upsell.addEventListener('click', this.onUpsellClick);
    }
  }

  open() {
    this.classList.add('is-open');
    if (this.overlay) {
      this.overlay.hidden = false;
      this.overlay.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-visible');
      this.overlay.addEventListener('click', this.onOverlayClick);
    }
    document.body.classList.add('cart-notification-open');
    this.setChatHidden(true);
    this.startChatWatch();

    // Double rAF so display:flex applies before transform transition runs
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.notification.classList.add('animate', 'active');
        this.notification.focus();
        trapFocus(this.notification);
      });
    });

    document.body.removeEventListener('click', this.onBodyClick);
    document.body.addEventListener('click', this.onBodyClick);
    this.loadUpsellDefault();
  }

  close() {
    this.notification.classList.remove('active', 'animate');
    this.classList.remove('is-open');
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.hidden = true;
      this.overlay.removeEventListener('click', this.onOverlayClick);
    }
    document.body.classList.remove('cart-notification-open');
    this.stopChatWatch();
    this.setChatHidden(false);
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  startChatWatch() {
    this.stopChatWatch();
    // Inbox may reinject the activator after open; keep it suppressed while drawer is open.
    this._chatWatch = window.setInterval(() => this.setChatHidden(true), 400);
  }

  stopChatWatch() {
    if (this._chatWatch) {
      window.clearInterval(this._chatWatch);
      this._chatWatch = null;
    }
  }

  setChatHidden(hidden) {
    const nodes = document.querySelectorAll(
      'shopify-chat, #dummy-chat-button-iframe, #ShopifyChat, #shopify-chat, .shopify-chat, iframe[title*="Chat"], iframe[title*="chat"]'
    );
    nodes.forEach((el) => {
      if (hidden) {
        el.setAttribute('data-cfd-chat-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      } else if (el.getAttribute('data-cfd-chat-hidden') === 'true') {
        el.removeAttribute('data-cfd-chat-hidden');
        el.style.removeProperty('display');
        el.style.removeProperty('opacity');
        el.style.removeProperty('visibility');
        el.style.removeProperty('pointer-events');
      }
    });
  }

  renderContents(parsedState) {
    this.lineItemId = parsedState.id;
    this.productId = parsedState.product_id || null;
    this.tabCache.clear();
    this.activeTabKey = null;

    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!el || !sectionHtml) return;
      let html = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (html == null && section.id === 'cart-notification-product') {
        html = this.getSectionInnerHTML(sectionHtml, '.cart-item');
      }
      if (html == null) return;
      el.innerHTML = html;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `#cart-notification-product-${this.lineItemId}`,
      },
      {
        id: 'cart-notification-progress',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const root = new DOMParser().parseFromString(html, 'text/html');
    const node = root.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target.closest('#cart-notification')) return;
    if (target.closest('.cart-notification-overlay')) return;

    const disclosure = target.closest('details-disclosure');
    this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
    this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  loadUpsellDefault() {
    if (!this.upsell || !this.upsellPanel) return;

    const oftenTab = this.upsell.querySelector('[data-tab-key="often"]');
    if (oftenTab) {
      this.activateTab(oftenTab);
      return;
    }

    const firstCollection = this.upsell.querySelector('[data-fetch-type="collection"]');
    if (firstCollection) this.activateTab(firstCollection);
  }

  handleUpsellClick(event) {
    const tab = event.target.closest('[data-tab-key]');
    if (tab && this.upsell.contains(tab)) {
      event.preventDefault();
      this.activateTab(tab);
      return;
    }

    const addBtn = event.target.closest('[data-variant-id]');
    if (addBtn && this.upsell.contains(addBtn)) {
      event.preventDefault();
      this.quickAdd(addBtn);
    }
  }

  activateTab(tab) {
    if (!tab || !this.upsellPanel) return;

    const key = tab.dataset.tabKey;
    this.upsell.querySelectorAll('[data-tab-key]').forEach((btn) => {
      const active = btn === tab;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.activeTabKey = key;

    if (this.tabCache.has(key)) {
      this.upsellPanel.innerHTML = this.tabCache.get(key);
      return;
    }

    this.showSkeleton();
    this.fetchTab(tab);
  }

  showSkeleton() {
    this.upsellPanel.innerHTML =
      '<div class="cart-notification-upsell__skeleton" aria-hidden="true"><span></span><span></span><span></span></div>';
  }

  fetchTab(tab) {
    const requestId = ++this._upsellRequestId;
    const key = tab.dataset.tabKey;
    const type = tab.dataset.fetchType;
    let url = tab.dataset.fetchUrl || '';

    if (type === 'recommendations') {
      const base = this.upsell.dataset.recommendationsBase;
      if (!base || !this.productId) {
        this.loadFallbackCollection(requestId, key);
        return;
      }
      url = `${base}&product_id=${encodeURIComponent(this.productId)}`;
    }

    if (!url) {
      this.upsellPanel.innerHTML =
        '<div class="cart-notification-upsell__empty">No products available</div>';
      return;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Upsell fetch failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (requestId !== this._upsellRequestId) return;

        const html = this.extractUpsellListHTML(text);
        const empty = !html || html.includes('data-upsell-empty');

        if (type === 'recommendations' && empty) {
          this.loadFallbackCollection(requestId, key);
          return;
        }

        const content =
          html ||
          '<div class="cart-notification-upsell__empty">No products available</div>';
        this.tabCache.set(key, content);
        if (this.activeTabKey === key) {
          this.upsellPanel.innerHTML = content;
        }
      })
      .catch((e) => {
        if (requestId !== this._upsellRequestId) return;
        console.error(e);
        if (type === 'recommendations') {
          this.loadFallbackCollection(requestId, key);
          return;
        }
        this.upsellPanel.innerHTML =
          '<div class="cart-notification-upsell__empty">Unable to load products</div>';
      });
  }

  loadFallbackCollection(requestId, oftenKey) {
    const fallbackUrl = this.upsell.dataset.fallbackUrl;

    if (!fallbackUrl) {
      if (requestId !== this._upsellRequestId) return;
      this.upsellPanel.innerHTML =
        '<div class="cart-notification-upsell__empty">No recommendations yet</div>';
      return;
    }

    fetch(fallbackUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Fallback fetch failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (requestId !== this._upsellRequestId) return;
        const html =
          this.extractUpsellListHTML(text) ||
          '<div class="cart-notification-upsell__empty">No products available</div>';
        this.tabCache.set(oftenKey || 'often', html);
        if (!this.activeTabKey || this.activeTabKey === (oftenKey || 'often')) {
          this.upsellPanel.innerHTML = html;
        }
      })
      .catch((e) => {
        if (requestId !== this._upsellRequestId) return;
        console.error(e);
        this.upsellPanel.innerHTML =
          '<div class="cart-notification-upsell__empty">Unable to load products</div>';
      });
  }

  extractUpsellListHTML(text) {
    const root = new DOMParser().parseFromString(text, 'text/html');
    const section =
      root.querySelector('.cart-notification-upsell-section') ||
      root.querySelector('.shopify-section');
    if (!section) return null;
    return section.innerHTML.trim() || null;
  }

  quickAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');

    const routesObj = window.routes || {};
    let endpoint = this.upsell?.dataset?.addUrl || routesObj.cart_add_url || '/cart/add';
    if (!endpoint.endsWith('.js')) {
      endpoint = `${endpoint}.js`;
    }

    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          throw new Error(data.description || 'Add to cart failed');
        }
        return this.refreshCartSections();
      })
      .catch((e) => console.error(e))
      .finally(() => button.classList.remove('is-loading'));
  }

  refreshCartSections() {
    const sections = ['cart-notification-progress', 'cart-icon-bubble', 'cart-notification-button'];
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}?sections=${sections.join(',')}`;

    return fetch(url)
      .then((response) => response.json())
      .then((data) => {
        sections.forEach((id) => {
          const el = document.getElementById(id);
          const sectionHtml = data[id];
          if (!el || !sectionHtml) return;
          const html = this.getSectionInnerHTML(sectionHtml);
          if (html == null) return;
          el.innerHTML = html;
        });
      });
  }
}

customElements.define('cart-notification', CartNotification);
